import {
    ScheduleV3Schedule,
    ScheduleV3Timeoff,
    ScheduleV3Unavailability,
    ScheduleV3Holiday,
    ScheduleV3Conflict,
    ScheduleV3Group,
} from "../../types/model/scheduleV3";

export interface ConflictContext {
    date: string;
    employeeId: string;
    sameDayEmployeeSchedules: ScheduleV3Schedule[];
    timeoff: ScheduleV3Timeoff[];
    unavailability: ScheduleV3Unavailability[];
    holiday: ScheduleV3Holiday[];
}

function isUnpublished(schedule: ScheduleV3Schedule): boolean {
    const publishStatus = schedule.publish;
    if (publishStatus === null || publishStatus === undefined) return true;
    const numericStatus = typeof publishStatus === "string" ? parseInt(publishStatus, 10) : publishStatus;
    return numericStatus === 0 || Number.isNaN(numericStatus);
}

function shouldReportOverlapConflict(
    schedule: ScheduleV3Schedule,
    other: ScheduleV3Schedule
): boolean {
    const scheduleIsUnpublished = isUnpublished(schedule);
    const otherIsUnpublished = isUnpublished(other);

    // When only one side is newly created/unpublished, show the conflict on that shift.
    if (scheduleIsUnpublished !== otherIsUnpublished) {
        return scheduleIsUnpublished;
    }

    const scheduleId = schedule.os_emp_schedules_id;
    const otherId = other.os_emp_schedules_id;

    // Otherwise fall back to the newer schedule record.
    if (scheduleId != null && otherId != null) {
        return scheduleId > otherId;
    }

    if (scheduleId == null && otherId != null) return true;
    if (scheduleId != null && otherId == null) return false;

    return true;
}

function normalizeTime(t?: string | null): string {
    if (!t) return "";
    if (t.length === 5 && t.includes(":")) return `${t}:00`;
    return t;
}

function extractTime(datetime?: string | null): string {
    if (!datetime) return "";
    // Match HH:mm or HH:mm:ss within a datetime string
    const m = datetime.match(/(\d{2}:\d{2}(:\d{2})?)/);
    if (!m) return "";
    return normalizeTime(m[1]);
}

function timeRangesOverlap(aStart: string, aEnd: string, bStart: string, bEnd: string): boolean {
    const as = normalizeTime(aStart);
    const ae = normalizeTime(aEnd);
    const bs = normalizeTime(bStart);
    const be = normalizeTime(bEnd);
    if (!as || !ae || !bs || !be) return false;
    return as < be && bs < ae;
}

function formatTime12h(t: string): string {
    const m = t.match(/^(\d{2}):(\d{2})/);
    if (!m) return t;
    let hour = parseInt(m[1], 10);
    const minute = m[2];
    const period = hour >= 12 ? "PM" : "AM";
    hour = hour % 12;
    if (hour === 0) hour = 12;
    return minute === "00" ? `${hour} ${period}` : `${hour}:${minute} ${period}`;
}

function makeConflict(
    schedule: ScheduleV3Schedule,
    type: ScheduleV3Conflict["type"],
    date: string,
    message: string
): ScheduleV3Conflict {
    return {
        type,
        scheduleId: String(schedule.os_emp_schedules_id ?? ""),
        employeeId: String(schedule.employee?.id ?? ""),
        date,
        message,
    };
}

export function getScheduleConflicts(
    schedule: ScheduleV3Schedule,
    ctx: ConflictContext
): ScheduleV3Conflict[] {
    const conflicts: ScheduleV3Conflict[] = [];
    const selfId = schedule.os_emp_schedules_id;
    const selfStart = normalizeTime(schedule.shift?.start_time);
    const selfEnd = normalizeTime(schedule.shift?.end_time);

    // Check 1: Shift-vs-shift overlap (same employee, same date)
    for (const other of ctx.sameDayEmployeeSchedules) {
        if (other.os_emp_schedules_id === selfId) continue;
        // Only report once per pair, preferring the newly created/unpublished shift.
        if (!shouldReportOverlapConflict(schedule, other)) continue;
        const otherStart = normalizeTime(other.shift?.start_time);
        const otherEnd = normalizeTime(other.shift?.end_time);
        if (timeRangesOverlap(selfStart, selfEnd, otherStart, otherEnd)) {
            conflicts.push(
                makeConflict(
                    schedule,
                    "overlap",
                    ctx.date,
                    `Overlaps ${other.shift?.shift_name || "another shift"} (${otherStart || "?"}-${otherEnd || "?"})`
                )
            );
        }
    }

    // Check 2: Approved time-off
    for (const to of ctx.timeoff) {
        const status = to.status;
        const isApproved = !status || String(status).toUpperCase().trim() === "APPROVED";
        if (!isApproved) continue;

        const toStart = extractTime(to.start_datetime);
        const toEnd = extractTime(to.end_datetime);
        // Note: `show_time` is a display preference (whether to show the time range in the UI),
        // not a signal that the time-off spans the full day — it must not affect this decision.
        const isFullDay =
            (to.hours != null && to.hours >= 24) ||
            !toStart ||
            !toEnd ||
            toStart === toEnd;

        const overlaps = isFullDay ? true : timeRangesOverlap(selfStart, selfEnd, toStart, toEnd);

        if (overlaps) {
            const reason = to.earnings_code_name || "time off";
            const windowText = isFullDay ? "full day" : `${formatTime12h(toStart)} – ${formatTime12h(toEnd)}`;
            conflicts.push(
                makeConflict(
                    schedule,
                    "timeoff",
                    ctx.date,
                    `Overlaps approved ${reason} time off (${windowText})`
                )
            );
            break; // one message per shift is enough
        }
    }

    // Check 3: Unavailability
    for (const un of ctx.unavailability) {
        if (un.all_day) {
            conflicts.push(makeConflict(schedule, "unavailable", ctx.date, "Employee unavailable (all day)"));
            break;
        }
        const unStart = normalizeTime(un.start_time);
        const unEnd = normalizeTime(un.end_time);
        if (unStart && unEnd) {
            if (timeRangesOverlap(selfStart, selfEnd, unStart, unEnd)) {
                conflicts.push(
                    makeConflict(
                        schedule,
                        "unavailable",
                        ctx.date,
                        `Employee unavailable (${unStart}-${unEnd})`
                    )
                );
                break;
            }
        } else {
            // No time window specified — treat as all day
            conflicts.push(makeConflict(schedule, "unavailable", ctx.date, "Employee unavailable"));
            break;
        }
    }

    // Check 4: Holiday (already filtered to this employee by buildConflictIndex)
    for (const hol of ctx.holiday) {
        if (hol.all_day) {
            conflicts.push(makeConflict(schedule, "holiday", ctx.date, `Holiday: ${hol.title}`));
            break;
        }
        const holStart = normalizeTime(hol.start_time);
        const holEnd = normalizeTime(hol.end_time);
        if (holStart && holEnd) {
            if (timeRangesOverlap(selfStart, selfEnd, holStart, holEnd)) {
                conflicts.push(makeConflict(schedule, "holiday", ctx.date, `Holiday: ${hol.title}`));
                break;
            }
        } else {
            // No time window — treat as all day
            conflicts.push(makeConflict(schedule, "holiday", ctx.date, `Holiday: ${hol.title}`));
            break;
        }
    }

    return conflicts;
}

export function buildConflictIndex(groups: ScheduleV3Group[]): Map<number, ScheduleV3Conflict[]> {
    type EmpDateBucket = {
        schedules: ScheduleV3Schedule[];
        timeoff: ScheduleV3Timeoff[];
        unavailability: ScheduleV3Unavailability[];
        holiday: ScheduleV3Holiday[];
    };
    const buckets = new Map<string, EmpDateBucket>();

    function getBucket(empId: string, date: string): EmpDateBucket {
        const key = `${date}|${empId}`;
        let b = buckets.get(key);
        if (!b) {
            b = { schedules: [], timeoff: [], unavailability: [], holiday: [] };
            buckets.set(key, b);
        }
        return b;
    }

    // Pass A: bucket everything by date+employeeId
    for (const group of groups) {
        if (!group.dates) continue;
        for (const [date, dateData] of Object.entries(group.dates)) {
            for (const schedule of dateData.schedules ?? []) {
                const empId = String(schedule.employee?.id ?? "");
                if (!empId) continue;
                getBucket(empId, date).schedules.push(schedule);
            }
            for (const to of dateData.timeoff ?? []) {
                const empId = String(to.employee?.id ?? "");
                if (!empId) continue;
                getBucket(empId, date).timeoff.push(to);
            }
            for (const un of dateData.unavailability ?? []) {
                const empId = String(un.employee?.id ?? "");
                if (!empId) continue;
                getBucket(empId, date).unavailability.push(un);
            }
            for (const hol of dateData.holiday ?? []) {
                for (const he of hol.employees ?? []) {
                    const empId = String(he.employee?.id ?? "");
                    if (!empId) continue;
                    getBucket(empId, date).holiday.push(hol);
                }
            }
        }
    }

    // Pass B: compute conflicts per schedule
    const result = new Map<number, ScheduleV3Conflict[]>();
    buckets.forEach((bucket, key) => {
        const pipeIdx = key.indexOf("|");
        const date = key.slice(0, pipeIdx);
        const empId = key.slice(pipeIdx + 1);
        // Dedup schedules by os_emp_schedules_id
        const seen = new Set<number>();
        const dedupedSchedules: ScheduleV3Schedule[] = [];
        bucket.schedules.forEach((s) => {
            if (s.os_emp_schedules_id == null || !seen.has(s.os_emp_schedules_id)) {
                if (s.os_emp_schedules_id != null) seen.add(s.os_emp_schedules_id);
                dedupedSchedules.push(s);
            }
        });

        dedupedSchedules.forEach((schedule) => {
            if (schedule.os_emp_schedules_id == null) return;
            const ctx: ConflictContext = {
                date,
                employeeId: empId,
                sameDayEmployeeSchedules: dedupedSchedules,
                timeoff: bucket.timeoff,
                unavailability: bucket.unavailability,
                holiday: bucket.holiday,
            };
            const conflicts = getScheduleConflicts(schedule, ctx);
            if (conflicts.length > 0) {
                result.set(schedule.os_emp_schedules_id, conflicts);
            }
        });
    });

    return result;
}

export function getConflictSeverity(conflicts: ScheduleV3Conflict[]): {
    color: "red" | "amber";
    ring: string;
    badge: string;
} | null {
    if (!conflicts.length) return null;
    const hasRed = conflicts.some((c) => c.type === "timeoff" || c.type === "unavailable");
    return hasRed
        ? { color: "red", ring: "ring-2 ring-red-500", badge: "text-red-600 bg-white" }
        : { color: "amber", ring: "ring-2 ring-amber-400", badge: "text-amber-600 bg-white" };
}
